import { getAccessToken, getRefreshToken, storeTokens, clearAuthData } from "@/services/auth";
import { API_BASE_URL } from "@/config";

const API_BASE = API_BASE_URL;
const BASE_PATH = "/admin/api/email-playground";

async function tryRefreshToken(): Promise<boolean> {
	const refreshToken = getRefreshToken();
	if (!refreshToken) return false;

	try {
		const response = await fetch(`${API_BASE}/admin/api/auth/refresh-token`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
		});

		if (!response.ok) return false;

		const data = await response.json();
		if (data.success && data.data) {
			storeTokens(data.data.accessToken, data.data.refreshToken);
			return true;
		}
		return false;
	} catch {
		return false;
	}
}

function buildErrorMessage(body: { message?: string; data?: { errors?: string[] } }): string {
	let msg = body.message || "API request failed";
	if (Array.isArray(body.data?.errors) && body.data.errors.length > 0) {
		msg += `: ${body.data.errors.join(", ")}`;
	}
	return msg;
}

async function fetchEmailPlaygroundApi<T>(
	endpoint: string,
	options?: RequestInit,
): Promise<T> {
	const token = getAccessToken();

	const response = await fetch(`${API_BASE}${BASE_PATH}${endpoint}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...options?.headers,
		},
	});

	if (response.status === 401) {
		const refreshed = await tryRefreshToken();
		if (refreshed) {
			const newToken = getAccessToken();
			const retryResponse = await fetch(`${API_BASE}${BASE_PATH}${endpoint}`, {
				...options,
				headers: {
					"Content-Type": "application/json",
					...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
					...options?.headers,
				},
			});

			if (!retryResponse.ok) {
				const error = await retryResponse.json().catch(() => ({}));
				throw new Error(buildErrorMessage(error) || `HTTP ${retryResponse.status}`);
			}

			const data = await retryResponse.json();
			if (!data.success) {
				throw new Error(buildErrorMessage(data));
			}

			return data.data ?? data;
		}
		clearAuthData();
		window.location.href = "/login";
		throw new Error("Session expired. Please login again.");
	}

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(buildErrorMessage(error) || `HTTP ${response.status}`);
	}

	const data = await response.json();
	if (!data.success) {
		throw new Error(buildErrorMessage(data));
	}

	return data.data ?? data;
}

// ==========================================
// Types
// ==========================================

export interface PlaygroundField {
	name: string;
	label: string;
	type: "string" | "number" | "date" | "url" | "boolean";
	sample: string | number | boolean;
}

export interface PlaygroundCatalogEntry {
	key: string;
	label: string;
	group: "notification" | "transactional" | "report";
	fields: PlaygroundField[];
}

export interface PreviewResult {
	subject: string;
	html: string;
}

// ==========================================
// API Functions
// ==========================================

export function getCatalog(): Promise<PlaygroundCatalogEntry[]> {
	return fetchEmailPlaygroundApi("/catalog", { method: "GET" });
}

export function previewEmail(
	key: string,
	data: Record<string, unknown>,
): Promise<PreviewResult> {
	return fetchEmailPlaygroundApi("/preview", {
		method: "POST",
		body: JSON.stringify({ key, data }),
	});
}

export function sendEmail(
	key: string,
	data: Record<string, unknown>,
	to: string,
): Promise<{ messageId?: string }> {
	return fetchEmailPlaygroundApi("/send", {
		method: "POST",
		body: JSON.stringify({ key, data, to }),
	});
}
